import styled from "@emotion/styled";
import { TextField } from "@mui/material";
import { AllJobs } from "lib/jobs";
import React, { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { trackCareerFreeSearch } from "reusable/gtm";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/effect-fade";
import "swiper/css/grid";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Desktop,
  ImageContainer,
  Mobile,
  NewText,
  TitleWithLine,
  useIsDesktop
} from "ui";
import { sharedColors } from "ui/theme/colors";
import { debounceBuilder } from "utils/utils";
import DropBox from "./Dropbox";
import JobCard from "./JobCard";
import OtherDropBox from "./OtherDropBox";

// ─── STYLED COMPONENTS (unchanged) ──────────────────────────────

const StyledContainer = styled.section`
  display: flex;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.down("md")} {
    padding-bottom: 40px;
    padding-left: 20px;
    padding-right: 20px;
  }
  padding-top: 30px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 50px;
    padding-left: 150px;
    padding-right: 150px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding-left: 220px;
    padding-right: 220px;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledJobs = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 40px;
  z-index: 10;
`;

const StyledPolygon = styled.div`
  position: absolute;
  right: 0;
  z-index: -1;
`;

const SwiperContainer = styled.div`
  margin-top: 32px;
  ${(props) => props.theme.breakpoints.down("md")} {
    margin-top: 17px;
  }
`;

const StyledActionsContainer = styled.div`
  display: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 40px;
    margin-bottom: 80px;
    gap: 47px;
    height: 49px;
  }
`;

const StyledArrowsContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledProgressBarContainer = styled.div`
  height: 1px;
  background: ${sharedColors.sliderBlue};
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 750px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 1000px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    width: 1127px;
  }
`;

const StyledNumbersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StyledArrowContainer = styled.div`
  cursor: pointer;
`;

const StyledBar = styled.div<{
  activeIndex: number;
  length: number;
  isDesktop: boolean;
}>`
  /* For example, we calculate a proportional width.
     You can adjust this formula to match your design. */
  width: ${(props) =>
    `calc((750px / ${props.length}) * ${props.activeIndex + (props.isDesktop ? 1 : 1)})`};
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: ${(props) =>
    `calc((1000px / ${props.length}) * ${props.activeIndex + (props.isDesktop ? 1 : 1)})`};
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    width: ${(props) =>
    `calc((1127px / ${props.length}) * ${props.activeIndex + (props.isDesktop ? 1 : 1)})`};
  }
  height: 3px;
  position: relative;
  top: -1px;
  background: ${sharedColors.sliderBlue};
`;

const StyledLine = styled.div`
  width: 45px;
  height: 2px;
  background: ${sharedColors.darkestBlue};
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  ${(props) => props.theme.breakpoints.down("md")} {
    grid-column: span 2;
    padding-bottom: 4px;
    border-bottom: 1px solid #5c6474b2;
  }
  .MuiInput-root {
    display: flex;
    align-items: center;
  }
  padding-left: 10px;

  input {
    color: #153e69;
    border: none;
    outline: none;
    font-size: 16px;
    line-height: 24px;
    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 12px;
      line-height: 16px;
    }
    ${(props) => props.theme.breakpoints.up("lg")} {
      font-size: 16px;
      line-height: 24px;
    }
    ::placeholder {
      color: #153e69;
      font-size: 16px;
      line-height: 24px;
      ${(props) => props.theme.breakpoints.up("md")} {
        font-size: 12px;
        line-height: 16px;
      }
      ${(props) => props.theme.breakpoints.up("lg")} {
        font-size: 16px;
        line-height: 24px;
      }
    }
  }
`;

const SelectContainer = styled.div<{ main?: boolean }>`
  ${(props) => props.theme.breakpoints.down("md")} {
    display: flex;
    justify-content: center;
    border-bottom: 1px solid #5c6474b2;
    ${(props) => props.main && `grid-column:span 2;`};
    .MuiSelect-select {
      padding-bottom: 0;
      padding-top: 0;
    }
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    border-right: 1px solid #bad2d4;
    padding-right: 16px;
    padding-left: 10px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    border-right: 1px solid #bad2d4;
    padding-right: 24px;
    padding-left: 16px;
  }
`;

const StyledFilter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 44px 16px 44px 16px;
  border-radius: 12px;
  border: 0.67px solid #153e6924;
  box-shadow: 0px 0px 13.333333969116211px 0px #153e691f;
  background-color: white;

  ${(props) => props.theme.breakpoints.down("md")} {
    gap: 20px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    border-radius: 32px;
    grid-template-columns: auto auto auto 2fr;
    padding: 5px 6px 5px 18px;
    height: 52px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    padding: 8px 10px 8px 28px;
  }
`;

const NoResults = styled.div`
  padding-top: 32px;
  padding-bottom: 150px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 72px;
    padding-bottom: 300px;
  }
`;

const StyledSwiperSlide = styled(SwiperSlide)`
  height: auto !important;
`;

// ─── TYPES ────────────────────────────────────────────────

export type CategoryState = {
  [key: string]: {
    checked: boolean;
    id: number;
  };
};

export type OtherState = {
  title: string;
  checked: boolean;
}[];

export type CategoriesArr = {
  url: string;
  id: number;
  name: string;
  title: string;
  numberOfJobs: number;
}[];

type Props = {
  jobs: AllJobs;
  setCategoryState: Dispatch<SetStateAction<CategoryState>>;
  categoriesState: CategoryState;
  companyState: OtherState;
  setCompanyState: Dispatch<SetStateAction<OtherState>>;
  locationState: OtherState;
  setLocationState: Dispatch<SetStateAction<OtherState>>;
};

// ─── HELPER FUNCTION TO GROUP JOBS ──────────────────────────────

function groupJobs(jobs: AllJobs, groupSize: number): AllJobs[] {
  const groups = [];
  for (let i = 0; i < jobs.length; i += groupSize) {
    groups.push(jobs.slice(i, i + groupSize));
  }
  return groups;
}

// ─── FILTER FUNCTION ─────────────────────────────────────────────

function filterJobsByCategory(
  jobs: AllJobs,
  categoryState: CategoryState,
  companyState: OtherState,
  locationState: OtherState,
  term: string
): AllJobs {
  const checkedCategoryIds = Object.values(categoryState)
    .filter((category) => category.checked)
    .map((category) => category.id);
  const checkedCompanies = companyState
    .filter((el) => el.checked)
    .map((el) => el.title);
  const checkedLocations = locationState
    .filter((el) => el.checked)
    .map((el) => el.title);
  return jobs.filter((job) => {
    const categoryCheck = checkedCategoryIds.includes(job.parentId);
    const companyCheck = job.companies.some((el) =>
      checkedCompanies.includes(el)
    );
    const locationCheck = job.location.some((el) =>
      checkedLocations.includes(el)
    );
    const termCheck = job.jobTitle?.includes(term);
    return categoryCheck && companyCheck && locationCheck && termCheck;
  });
}

// ─── COMPONENT ─────────────────────────────────────────────────

function JobsSearch({
  jobs,
  categoriesState,
  setCategoryState,
  companyState,
  locationState,
  setCompanyState,
  setLocationState,
}: Props) {
  const [activeIndex, setActiveIndex] = useState(0); // represents the current page (0-based)
  const [term, setTerm] = useState("");
  const isDesktop = useIsDesktop();

  // Debounce free search tracking
  const trachDebouncedEvent = useCallback(
    debounceBuilder(trackCareerFreeSearch, 1000),
    [trackCareerFreeSearch]
  );

  const handleTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTerm(e.target.value);
    trachDebouncedEvent(e.target.value);
  };

  // First, filter jobs
  const filteredJobs = filterJobsByCategory(
    jobs,
    categoriesState,
    companyState,
    locationState,
    term
  );

  // For desktop, group jobs into pages of 6; for mobile, each slide is a single job
  const jobPages = isDesktop
    ? groupJobs(filteredJobs, 6)
    : filteredJobs.map((job) => [job]);
  const totalPages = jobPages.length;

  // We'll store the swiper instance in a ref
  const swiperRef = useRef<any>(null);

  // Custom arrow handlers
  const handleNext = () => {
    if (swiperRef.current && activeIndex < totalPages - 1) {
      swiperRef.current.slideNext();
    }
  };

  const handlePrev = () => {
    if (swiperRef.current && activeIndex > 0) {
      swiperRef.current.slidePrev();
    }
  };

  return (
    <StyledContainer id="jobs" tabIndex={-1}>
      <StyledHeader>
        <StyledPolygon>
          <ImageContainer
            desktopUrl={"/images/polygonjob.png"}
            mobileUrl={"/images/polygonjobmobile.png"}
            height={300}
            width={500}
            mediumHeight={300}
            mediumWidth={500}
            mobileHeight={260}
            mobileWidth={220}
            sm
            fill
          />
        </StyledPolygon>
        <Desktop>
          <NewText
            component={"h2"}
            largeDesktopFontSize={64}
            largeDesktopLineHeight="72px"
            desktopFontWeight={500}
            desktopFontSize={42}
            desktopLineHeight="48px"
          >
            פשוט למצוא את הדרך שלך
          </NewText>
        </Desktop>
        <Mobile>
          <TitleWithLine
            noPadding={true}
            title={"פשוט למצוא את הדרך שלך"}
            titleColor={sharedColors.black}
            titleFontSize={64}
            mobileTitleFontSize={36}
            component={"h2"}
          />
        </Mobile>
        <Desktop>
          <TitleWithLine
            noPadding={false}
            title={`${filteredJobs?.length} משרות פתוחות`}
            titleColor={sharedColors.black}
            titleFontSize={24}
            smallDesktopLineHeight="30px"
            largeDesktopFontSize={36}
            largeDesktopLineHeight="52px"
            desktopFontWeight={400}
            component={"p"}
          />
        </Desktop>
        <Mobile>
          <NewText
            largeDesktopFontSize={36}
            largeDesktopLineHeight="52px"
            desktopFontSize={24}
            desktopLineHeight={"34px"}
            mobileFontSize={18}
            mobileLineHeight={"25.2px"}
            color="black"
            component={"p"}
            desktopFontWeight={500}
          >{`${filteredJobs?.length} משרות פתוחות`}</NewText>
        </Mobile>
      </StyledHeader>

      <StyledJobs>
        <StyledFilter>
          <SelectContainer main={true}>
            <DropBox
              categoryState={categoriesState}
              setCategoryState={setCategoryState}
            />
          </SelectContainer>
          <SelectContainer>
            <OtherDropBox
              categoryState={locationState}
              setCategoryState={setLocationState}
              titleHeader="שממוקמת ב..."
              label="מיקום"
            />
          </SelectContainer>
          <SelectContainer>
            <OtherDropBox
              categoryState={companyState}
              setCategoryState={setCompanyState}
              titleHeader="שם חברה"
              label="חברה"
            />
          </SelectContainer>
          <StyledTextField
            fullWidth
            onChange={handleTermChange}
            placeholder="חיפוש חופשי"
            value={term}
            role="form"
            aria-label="חיפוש חופשי ובמשך לתוצאות מסוננות"
            InputProps={{
              disableUnderline: true,
            }}
            label=""
            variant="standard"
          />
        </StyledFilter>

        {filteredJobs.length > 0 ? (
          <>
            <SwiperContainer>
              <Swiper
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                slidesPerView={1} // each slide is now a page (group) of jobs
                navigation={false} // we handle arrows manually
                style={{ padding: isDesktop ? 20 : 0 }}
                modules={[Navigation]}
                onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                spaceBetween={isDesktop ? 24 : 12}
              >
                {jobPages.map((page, index) => (
                  <StyledSwiperSlide key={index}>
                    {isDesktop ? (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "24px",
                        }}
                      >
                        {page.map((job, idx) => (
                          <JobCard data={job} key={idx} />
                        ))}
                      </div>
                    ) : (
                      // Mobile: display one job per slide
                      <JobCard data={page[0]} />
                    )}
                  </StyledSwiperSlide>
                ))}
              </Swiper>
            </SwiperContainer>
            <StyledActionsContainer>
              <StyledNumbersContainer>
                <NewText
                  component="p"
                  desktopFontSize={24}
                  desktopLineHeight="28.8px"
                  color={sharedColors.blue}
                  smallDesktopFontSize={16}
                  smallDesktopLineHeight="19px"
                >
                  {`0${activeIndex + 1}`}
                </NewText>{" "}
                <StyledLine />{" "}
                <NewText
                  component="p"
                  desktopFontSize={24}
                  desktopLineHeight="28.8px"
                  color={sharedColors.darkestBlue}
                  smallDesktopFontSize={16}
                  smallDesktopLineHeight="19px"
                >
                  {`0${totalPages}`}
                </NewText>
              </StyledNumbersContainer>

              <StyledProgressBarContainer>
                <StyledBar
                  isDesktop={isDesktop}
                  activeIndex={activeIndex}
                  length={totalPages}
                />
              </StyledProgressBarContainer>
              <StyledArrowsContainer>
                <StyledArrowContainer className="arrow-right-jobs arrow" onClick={handlePrev}>
                  <ImageContainer
                    height={24}
                    desktopUrl={"/icons/Arrow/arrow-left.png"}
                    width={24}
                    alt="arrow-left"
                    mediumHeight={16}
                    mediumWidth={16}
                  />
                </StyledArrowContainer>
                <StyledArrowContainer className="arrow-left-jobs arrow" onClick={handleNext}>
                  <ImageContainer
                    height={24}
                    desktopUrl={"/icons/Arrow/arrow-right.png"}
                    width={24}
                    alt="arrow-right"
                    mediumHeight={16}
                    mediumWidth={16}
                  />
                </StyledArrowContainer>
              </StyledArrowsContainer>
            </StyledActionsContainer>
          </>
        ) : (
          <NoResults>
            <NewText
              component={"span"}
              desktopFontSize={28}
              desktopLineHeight={"39.2px"}
            >
              לא נמצאו תוצאות
            </NewText>
          </NoResults>
        )}
      </StyledJobs>
    </StyledContainer>
  );
}

export default JobsSearch;
